import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message, Modal } from "antd";
import Layout from "./common/Layout";
import Pagination from "./common/Pagination";
import { get_account_list } from "../apis/GetAccountList";
import { delete_incoming_account } from "../apis/DeleteIncomingAccount";
import "../css/myAccount.scss";
import Loading from "./common/Loading";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";

const MyAccount = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [accountData, setAccountData] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 搜索关键词状态
  const [searchKeyword, setSearchKeyword] = useState("");
  // 实际用于查询的关键词
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");
  // 防止重复调用
  const hasFetchedRef = useRef(false);

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 平台类型显示映射
  const getPlatformTypeLabel = (platformType) => {
    const typeMap = {
      ecommerce: "电商平台",
      service: "服务",
      other: "其他",
    };
    return typeMap[platformType] || platformType;
  };

  // 获取账户列表数据
  const fetchAccountList = async (
    page = currentPage,
    size = pageSize,
    search = activeSearchKeyword
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await get_account_list(search || "", page, size);

      if (response.data && response.data.data) {
        setAccountData(response.data.data.list || []);
        // 更新分页信息
        if (response.data.data.pagination) {
          setPagination(response.data.data.pagination);
          setCurrentPage(response.data.data.pagination.page);
          setPageSize(response.data.data.pagination.page_size);
        }
      } else {
        setError(response.data?.message || "获取账户列表失败");
      }
    } catch (err) {
      console.error("获取账户列表失败:", err);
      setError(err.response?.data?.message || "获取账户列表失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  // 组件加载时获取数据
  useEffect(() => {
    // 防止在 React Strict Mode 下重复调用
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchAccountList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 页码变化时重新获取数据
  const handlePageChange = (page) => {
    fetchAccountList(page, pageSize, activeSearchKeyword);
  };

  // 每页条数变化时重新获取数据
  const handlePageSizeChange = (size) => {
    fetchAccountList(1, size, activeSearchKeyword); // 改变每页条数时回到第一页
  };

  // 跳转到申请页面
  const handleApplyAccount = () => {
    navigate("/apply-ecommerce-account");
  };

  // 查看详情
  const handleViewDetail = (accountId) => {
    navigate(`/store-detail/${accountId}`);
  };

  // 编辑账户
  const handleEdit = (accountId) => {
    navigate(`/apply-ecommerce-account?id=${accountId}`);
  };

  // 删除账户
  const handleDelete = (account) => {
    Modal.confirm({
      title: "确认删除",
      content: `确定要删除账户"${account.platform_name}"吗？此操作不可恢复。`,
      okText: "确认删除",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          const response = await delete_incoming_account(account.id);
          if (response.data && response.data.data) {
            message.success("删除成功");
            // 刷新列表
            fetchAccountList(currentPage, pageSize, activeSearchKeyword);
          } else {
            message.error(response.data?.message || "删除失败");
          }
        } catch (error) {
          console.error("删除账户失败:", error);
          message.error(
            error.response?.data?.message || "删除失败，请稍后重试"
          );
        }
      },
    });
  };

  // 处理查询按钮点击
  const handleSearch = () => {
    setActiveSearchKeyword(searchKeyword); // 更新实际查询关键词
    fetchAccountList(1, pageSize, searchKeyword); // 使用新的搜索关键词进行查询
  };

  // 处理重置按钮点击
  const handleReset = () => {
    setSearchKeyword("");
    setActiveSearchKeyword(""); // 清空实际查询关键词
    fetchAccountList(1, pageSize, ""); // 使用空字符串重新查询
  };

  return (
    <Layout>
      <div className="my-account">
        {/* 标签页 */}
        <div className="tabs-container">
          <div className="tab-item active">电商收款账户</div>
        </div>

        {/* 筛选和操作按钮区域 */}
        <div className="filter-action-section">
          {/* 搜索区域 */}
          <div className="filter-section">
            <div className="filter-row">
              <input
                type="text"
                className="filter-input"
                placeholder="搜索平台名称、账号、平台类型、币种等"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
              <button className="btn-primary" onClick={handleSearch}>
                查询
              </button>
              <button className="btn-secondary" onClick={handleReset}>
                重置
              </button>
            </div>
          </div>

          <button className="btn-add" onClick={handleApplyAccount}>
            <span className="icon-plus">+</span> 申请电商收款账户
          </button>
        </div>

        {/* 表格 */}
        <div className="table-container">
          {loading ? (
            <Loading />
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button className="btn-retry" onClick={fetchAccountList}>
                重试
              </button>
            </div>
          ) : accountData.length === 0 ? (
            <div className="empty-container">
              <p className="empty-message">暂无账户数据</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>用户ID</th>

                  <th>平台名称</th>
                  <th>账户编号</th>
                  <th>币种</th>
                  <th>余额</th>
                  <th>平台类型</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {accountData.map((account) => (
                  <tr key={account.id}>
                    <td>{account.id}</td>
                    <td>{account.user_id}</td>
                    <td>{account.platform_name}</td>
                    <td>{account.account_number}</td>
                    <td>{account.currency}</td>
                    <td>{account.available_balance}</td>{" "}
                    <td>{getPlatformTypeLabel(account.platform_type)}</td>
                    <td>{account.account_status}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-detail"
                          onClick={() => handleViewDetail(account.id)}
                        >
                          详情
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(account.id)}
                        >
                          编辑
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(account)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 分页 */}
        {accountData.length > 0 && (
          <Pagination
            currentPage={pagination.page}
            pageSize={pagination.page_size}
            totalPages={pagination.total_pages}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>

      <footer className="page-footer">
        <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </footer>
    </Layout>
  );
};

export default MyAccount;
